/* =========================================================================
   UserProfitLoss.tsx – per-user betting summary
   Shows how many bets each user placed and how much they are up or down,
   combining in-house casino, third-party casino and sportsbook activity.
   ========================================================================= */
import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { BASE } from "../utils/api";

interface Row {
  userId: number;
  userName: string | null;
  casinoBets: number;
  huiduBets: number;
  sportsBets: number;
  totalBets: number;
  totalStaked: number;
  totalReturned: number;
  totalProfit: number;
  totalLoss: number;
  netPl: number;
}

interface Totals {
  userCount: number;
  totalBets: number;
  totalStaked: number;
  totalReturned: number;
  totalProfit: number;
  totalLoss: number;
  netPl: number;
}

interface ApiResponse {
  success: boolean;
  rows: Row[];
  totals: Totals;
  pagination: { page: number; limit: number; totalCount: number; totalPages: number };
  error?: string;
}

type SortKey = "bets" | "staked" | "profit" | "loss" | "net";

const EMPTY_TOTALS: Totals = {
  userCount: 0,
  totalBets: 0,
  totalStaked: 0,
  totalReturned: 0,
  totalProfit: 0,
  totalLoss: 0,
  netPl: 0
};

const money = (n: number): string =>
  n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PAGE_SIZE = 25;

const UserProfitLoss: React.FC = () => {
  const staffId = localStorage.getItem("currentUserId");

  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState<Totals>(EMPTY_TOTALS);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<SortKey>("bets");
  const [last24h, setLast24h] = useState(true);

  // Modal state for viewing user bet details
  const [showModal, setShowModal] = useState(false);
  const [modalUser, setModalUser] = useState<Row | null>(null);
  const [modalBets, setModalBets] = useState<any[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalDateFrom, setModalDateFrom] = useState("");
  const [modalDateTo, setModalDateTo] = useState("");
  const [modalLast24h, setModalLast24h] = useState(true);
  const MODAL_PAGE_SIZE = 10;

  const pickDateFrom = (v: string) => { setDateFrom(v); if (v) setLast24h(false); setPage(1); };
  const pickDateTo = (v: string) => { setDateTo(v); if (v) setLast24h(false); setPage(1); };
  const toggle24h = (on: boolean) => { setLast24h(on); if (on) { setDateFrom(""); setDateTo(""); } setPage(1); };

  // Compute the rolling 24h window dates to send to the backend
  const getEffectiveDates = () => {
    if (last24h) {
      const now = new Date();
      const from24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return {
        dateFrom: from24h.toISOString(),
        dateTo: now.toISOString()
      };
    }
    return { dateFrom, dateTo };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const effectiveDates = getEffectiveDates();
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        sort,
        ...(search ? { search } : {}),
        ...(effectiveDates.dateFrom ? { dateFrom: effectiveDates.dateFrom } : {}),
        ...(effectiveDates.dateTo ? { dateTo: effectiveDates.dateTo } : {})
      });

      const { data } = await axios.get<ApiResponse>(
        `${BASE}/userProfitLoss/summary?${params.toString()}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(staffId ? { "x-staff-id": staffId } : {})
          }
        }
      );

      if (data.success) {
        setRows(data.rows || []);
        setTotals(data.totals || EMPTY_TOTALS);
        setTotalPages(data.pagination?.totalPages || 1);
      } else {
        setError(data.error || "Failed to load profit/loss summary");
        setRows([]);
        setTotals(EMPTY_TOTALS);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load profit/loss summary");
      setRows([]);
      setTotals(EMPTY_TOTALS);
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFrom, dateTo, sort, last24h, staffId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const clearFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setSort("bets");
    setLast24h(true);
    setPage(1);
  };

  const hasFilters = Boolean(search || dateFrom || dateTo || sort !== "bets" || !last24h);

  const openBetModal = async (user: Row) => {
    setModalUser(user);
    setShowModal(true);
    setModalLoading(true);
    setModalBets([]);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(staffId ? { "x-staff-id": staffId } : {})
      };

      // Fetch user bet details (includes LuckySport with match info from backend)
      const [casinoRes] = await Promise.all([
        axios.get(`${BASE}/betHistory/transactions/user/${user.userId}?page=1&limit=100`, { headers })
      ]);

      const casinoBets = (casinoRes.data.transactions || []).map((b: any) => ({
        ...b,
        source: 'casino',
        date: b.transaction_timestamp
      }));

      // Group bets by round_id to merge BET + WIN/LOSS into single rows
      const roundGroups: Record<string, any[]> = {};
      const noRound: any[] = [];
      
      casinoBets.forEach((bet: any) => {
        if (bet.round_id) {
          if (!roundGroups[bet.round_id]) roundGroups[bet.round_id] = [];
          roundGroups[bet.round_id].push(bet);
        } else {
          noRound.push(bet);
        }
      });

      const mergedBets: any[] = [];

      Object.values(roundGroups).forEach((group: any[]) => {
        const betTx = group.find(t => t.transaction_type === 'BET');
        const winTx = group.find(t => t.transaction_type === 'WIN');
        const lossTx = group.find(t => t.transaction_type === 'LOSS');
        const resultTx = winTx || lossTx;
        const baseTx = betTx || resultTx || group[0];

        mergedBets.push({
          ...baseTx,
          game_title: baseTx.game_title,
          team_one: baseTx.team_one || (resultTx?.team_one) || '',
          team_two: baseTx.team_two || (resultTx?.team_two) || '',
          category: baseTx.category || (resultTx?.category) || '',
          transaction_type: resultTx ? resultTx.transaction_type : 'BET',
          amount: betTx ? betTx.amount : (resultTx?.amount || 0),
          result_amount: resultTx ? resultTx.amount : null,
          date: baseTx.date
        });
      });

      noRound.forEach((bet: any) => mergedBets.push(bet));

      const allBets = mergedBets.sort(
        (a: any, b: any) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
      );

      setModalBets(allBets);
    } catch (e) {
      console.error("Failed to fetch user bets:", e);
    } finally {
      setModalLoading(false);
    }
  };

  const cards: { label: string; value: string; tone?: "up" | "down" }[] = [
    { label: "Users", value: String(totals.userCount) },
    { label: "Total Bets", value: String(totals.totalBets) },
    { label: "Total Staked", value: `₹${money(totals.totalStaked)}` },
    { label: "Total Profit", value: `₹${money(totals.totalProfit)}`, tone: "up" },
    { label: "Total Loss", value: `₹${money(totals.totalLoss)}`, tone: "down" },
    {
      label: "Net P/L",
      value: `${totals.netPl < 0 ? "-" : ""}₹${money(Math.abs(totals.netPl))}`,
      tone: totals.netPl >= 0 ? "up" : "down"
    }
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-6 space-y-6">
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold text-white">User Profit / Loss</h1>
        <button
          onClick={fetchData}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
        >
          <RefreshCw className="w-4 h-4" /> reload
        </button>
      </div>

      {/* summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-lg shadow p-3">
            <div className="text-xs uppercase text-gray-500">{c.label}</div>
            <div
              className={`text-lg font-semibold ${
                c.tone === "up" ? "text-green-600" : c.tone === "down" ? "text-red-600" : "text-gray-900"
              }`}
            >
              {c.value}
            </div>
          </div>
        ))}
      </div>

      {/* filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search user</label>
            <input
              className="w-full p-2 border rounded-lg"
              placeholder="Name or user ID"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From date</label>
            <input
              type="date"
              className="w-full p-2 border rounded-lg"
              value={dateFrom}
              onChange={e => pickDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To date</label>
            <input
              type="date"
              className="w-full p-2 border rounded-lg"
              value={dateTo}
              onChange={e => pickDateTo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
            <select
              className="w-full p-2 border rounded-lg"
              value={sort}
              onChange={e => { setSort(e.target.value as SortKey); setPage(1); }}
            >
              <option value="bets">Most bets</option>
              <option value="staked">Highest staked</option>
              <option value="profit">Highest profit</option>
              <option value="loss">Highest loss</option>
              <option value="net">Best net P/L</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={last24h}
              onChange={e => toggle24h(e.target.checked)}
            />
            Last 24 hours only
          </label>
          <button onClick={clearFilters} className="px-4 py-2 bg-gray-200 rounded-md text-sm">
            Clear
          </button>
          <span className="text-sm text-gray-500">
            {last24h
              ? `Last 24 hours · ${totals.userCount} user(s) with bets`
              : dateFrom || dateTo
                ? `Filtered period · ${totals.userCount} user(s) with bets`
                : `All time · ${totals.userCount} user(s) with bets`}
          </span>
        </div>
      </div>

      {/* table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {["User", "Total Bets", "Casino", "Live Casino", "Sports", "Staked", "Returned", "Profit", "Loss", "Net P/L", "Action"].map(h => (
                <th
                  key={h}
                  className={`px-4 py-2 font-medium text-gray-500 uppercase text-xs whitespace-nowrap ${
                    h === "User" || h === "Action" ? "text-left" : "text-right"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="py-6 text-center">Loading…</td></tr>
            ) : error ? (
              <tr><td colSpan={11} className="py-6 text-center text-red-600">{error}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="py-6 text-center text-gray-500">No betting activity found</td></tr>
            ) : rows.map(r => (
              <tr key={r.userId} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{r.userName || "(unknown)"}</div>
                  <div className="text-xs text-gray-500">ID {r.userId}</div>
                </td>
                <td className="px-4 py-2 text-right font-medium">{r.totalBets}</td>
                <td className="px-4 py-2 text-right text-gray-600">{r.casinoBets}</td>
                <td className="px-4 py-2 text-right text-gray-600">{r.huiduBets}</td>
                <td className="px-4 py-2 text-right text-gray-600">{r.sportsBets}</td>
                <td className="px-4 py-2 text-right">{money(r.totalStaked)}</td>
                <td className="px-4 py-2 text-right">{money(r.totalReturned)}</td>
                <td className="px-4 py-2 text-right text-green-600">{money(r.totalProfit)}</td>
                <td className="px-4 py-2 text-right text-red-600">{money(r.totalLoss)}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <span
                    className={`font-semibold ${
                      r.netPl > 0 ? "text-green-600" : r.netPl < 0 ? "text-red-600" : "text-gray-700"
                    }`}
                  >
                    {r.netPl < 0 ? "-" : r.netPl > 0 ? "+" : ""}{money(Math.abs(r.netPl))}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => openBetModal(r)}
                    className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* pager */}
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="p-2 disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-600">Page {page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="p-2 disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Net P/L is from the player's point of view: a positive value means the user is up and the
        house is down. Casino figures come from settled rounds; sportsbook figures use settlement
        credits, so bets that are still open are counted as staked but not yet settled.
      </p>

      {/* Bet Details Modal */}
      {showModal && modalUser && ReactDOM.createPortal((() => {
        // Apply date filters on modal bets
        let filteredModalBets = modalBets;
        if (modalLast24h) {
          const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
          filteredModalBets = filteredModalBets.filter(b => new Date(b.date || b.transaction_timestamp) >= cutoff);
        }
        if (modalDateFrom) {
          const from = new Date(modalDateFrom); from.setHours(0,0,0,0);
          filteredModalBets = filteredModalBets.filter(b => new Date(b.date || b.transaction_timestamp) >= from);
        }
        if (modalDateTo) {
          const to = new Date(modalDateTo); to.setHours(23,59,59,999);
          filteredModalBets = filteredModalBets.filter(b => new Date(b.date || b.transaction_timestamp) <= to);
        }

        const modalTotalStaked = filteredModalBets.reduce((s, b) => s + parseFloat(b.amount || 0), 0);
        const modalNetPl = filteredModalBets.reduce((s, b) => {
          if (b.transaction_type === 'WIN') return s + parseFloat(b.result_amount || b.amount || 0);
          if (b.transaction_type === 'LOSS') return s - parseFloat(b.amount || 0);
          return s;
        }, 0);
        const modalTotalPages = Math.ceil(filteredModalBets.length / MODAL_PAGE_SIZE);
        const modalPagedBets = filteredModalBets.slice((modalPage - 1) * MODAL_PAGE_SIZE, modalPage * MODAL_PAGE_SIZE);

        return (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/70 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-[850px] max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Bet Details — {modalUser.userName || "(unknown)"}
                </h2>
                <p className="text-sm text-gray-500">User ID: {modalUser.userId}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openBetModal(modalUser)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-4 p-4 border-b bg-gray-50">
              <div className="text-center">
                <div className="text-xs text-gray-500 uppercase">Total Bets</div>
                <div className="text-lg font-semibold">{filteredModalBets.length}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 uppercase">Total Staked</div>
                <div className="text-lg font-semibold">₹{modalTotalStaked.toFixed(2)}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 uppercase">Net P/L</div>
                <div className={`text-lg font-semibold ${modalUser.netPl > 0 ? 'text-green-600' : modalUser.netPl < 0 ? 'text-red-600' : ''}`}>
                  {modalUser.netPl >= 0 ? '+' : ''}{modalUser.netPl.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 p-4 border-b flex-wrap">
              <label className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={modalLast24h} onChange={e => { setModalLast24h(e.target.checked); if(e.target.checked){setModalDateFrom('');setModalDateTo('');} setModalPage(1); }} />
                Last 24h
              </label>
              <input type="date" value={modalDateFrom} onChange={e => { setModalDateFrom(e.target.value); setModalLast24h(false); setModalPage(1); }} className="border rounded px-2 py-1 text-sm" />
              <input type="date" value={modalDateTo} onChange={e => { setModalDateTo(e.target.value); setModalLast24h(false); setModalPage(1); }} className="border rounded px-2 py-1 text-sm" />
              {(modalLast24h || modalDateFrom || modalDateTo) && (
                <button onClick={() => { setModalLast24h(false); setModalDateFrom(''); setModalDateTo(''); setModalPage(1); }} className="text-xs text-blue-600 hover:underline">Clear</button>
              )}
            </div>

            <div className="overflow-auto flex-1 p-4">
              {modalLoading ? (
                <div className="py-10 text-center text-gray-500">Loading bets...</div>
              ) : filteredModalBets.length === 0 ? (
                <div className="py-10 text-center text-gray-500">No bets found</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Game / Match</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Stake</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalPagedBets.map((bet: any, idx: number) => (
                      <tr key={bet.id || idx} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="font-medium">{bet.game_title === '大厅' ? 'LuckySport' : (bet.game_title || '-')}</div>
                          {bet.team_one && bet.team_two && (
                            <div className="text-xs text-gray-500">{bet.team_one} vs {bet.team_two}</div>
                          )}
                          {bet.category && (
                            <div className="text-xs text-gray-400">{bet.category}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{parseFloat(bet.amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            bet.transaction_type === 'WIN' ? 'bg-green-100 text-green-700' :
                            bet.transaction_type === 'LOSS' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {bet.transaction_type === 'WIN' ? `+${parseFloat(bet.result_amount || bet.amount || 0).toFixed(2)}` :
                             bet.transaction_type === 'LOSS' ? 'LOSS' :
                             'Pending'}
                          </span>
                        </td>
                        <td className="px-3 py-2">{bet.currency_code || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {bet.transaction_timestamp ? new Date(bet.transaction_timestamp).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination + Close */}
            <div className="p-4 border-t flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button disabled={modalPage <= 1} onClick={() => setModalPage(p => p - 1)} className="px-3 py-1 text-sm border rounded disabled:opacity-40">Previous</button>
                <span className="text-sm text-gray-600">{modalPage} / {modalTotalPages || 1}</span>
                <button disabled={modalPage >= modalTotalPages} onClick={() => setModalPage(p => p + 1)} className="px-3 py-1 text-sm border rounded disabled:opacity-40">Next</button>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })(), document.body)}
    </div>
  );
};

export default UserProfitLoss;
